import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ReactFlow,
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  Plus, Play, Pause, Trash2, Settings, Zap, Clock,
  Mail, MessageSquare, Tag, User, GitBranch, Bell,
  ChevronRight, Save, ArrowRight, X, Check, AlertCircle,
  TrendingUp, FileText, Edit3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom Node Components
const TriggerNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-xl border-2 bg-emerald-950/80 border-emerald-500 min-w-[200px] ${selected ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900' : ''}`}>
    <Handle type="source" position={Position.Bottom} className="!bg-emerald-500 !w-3 !h-3" />
    <div className="flex items-center gap-2 mb-1">
      <div className="p-1.5 rounded-lg bg-emerald-500/20">
        {data.icon || <Zap className="w-4 h-4 text-emerald-400" />}
      </div>
      <span className="font-semibold text-emerald-300 text-sm">Trigger</span>
    </div>
    <p className="text-white font-medium">{data.label}</p>
    {data.description && <p className="text-emerald-400/70 text-xs mt-1">{data.description}</p>}
  </div>
);

const ActionNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-xl border-2 bg-blue-950/80 border-blue-500 min-w-[200px] ${selected ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900' : ''}`}>
    <Handle type="target" position={Position.Top} className="!bg-blue-500 !w-3 !h-3" />
    <Handle type="source" position={Position.Bottom} className="!bg-blue-500 !w-3 !h-3" />
    <div className="flex items-center gap-2 mb-1">
      <div className="p-1.5 rounded-lg bg-blue-500/20">
        {data.icon || <Play className="w-4 h-4 text-blue-400" />}
      </div>
      <span className="font-semibold text-blue-300 text-sm">Action</span>
    </div>
    <p className="text-white font-medium">{data.label}</p>
    {data.description && <p className="text-blue-400/70 text-xs mt-1">{data.description}</p>}
  </div>
);

const ConditionNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-xl border-2 bg-emerald-950/80 border-emerald-500 min-w-[200px] ${selected ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900' : ''}`}>
    <Handle type="target" position={Position.Top} className="!bg-emerald-500 !w-3 !h-3" />
    <Handle type="source" position={Position.Bottom} id="yes" style={{ left: '30%' }} className="!bg-emerald-500 !w-3 !h-3" />
    <Handle type="source" position={Position.Bottom} id="no" style={{ left: '70%' }} className="!bg-red-500 !w-3 !h-3" />
    <div className="flex items-center gap-2 mb-1">
      <div className="p-1.5 rounded-lg bg-emerald-500/20">
        {data.icon || <GitBranch className="w-4 h-4 text-emerald-400" />}
      </div>
      <span className="font-semibold text-emerald-300 text-sm">Condition</span>
    </div>
    <p className="text-white font-medium">{data.label}</p>
    {data.description && <p className="text-emerald-400/70 text-xs mt-1">{data.description}</p>}
    <div className="flex justify-between mt-2 text-xs">
      <span className="text-emerald-400">Yes</span>
      <span className="text-red-400">No</span>
    </div>
  </div>
);

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
};

const NODE_ICONS = {
  trigger_lead_created: <User className="w-4 h-4 text-emerald-400" />,
  trigger_form_submitted: <FileText className="w-4 h-4 text-emerald-400" />,
  trigger_tag_added: <Tag className="w-4 h-4 text-emerald-400" />,
  trigger_score_changed: <TrendingUp className="w-4 h-4 text-emerald-400" />,
  trigger_manual: <Play className="w-4 h-4 text-emerald-400" />,
  action_send_email: <Mail className="w-4 h-4 text-blue-400" />,
  action_send_whatsapp: <MessageSquare className="w-4 h-4 text-green-400" />,
  action_wait: <Clock className="w-4 h-4 text-amber-400" />,
  action_add_tag: <Tag className="w-4 h-4 text-emerald-400" />,
  action_remove_tag: <X className="w-4 h-4 text-red-400" />,
  action_update_lead: <Edit3 className="w-4 h-4 text-emerald-400" />,
  action_enroll_sequence: <GitBranch className="w-4 h-4 text-pink-400" />,
  action_notify_team: <Bell className="w-4 h-4 text-orange-400" />,
  condition_if_else: <GitBranch className="w-4 h-4 text-emerald-400" />,
  condition_score_check: <TrendingUp className="w-4 h-4 text-emerald-400" />,
  condition_tag_check: <Tag className="w-4 h-4 text-emerald-400" />,
};

export default function Automations() {
  // Get business content for automation sequences
  const { getAutomationSequence, getBusinessTypeName, businessType } = useBusinessContent();
  
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [nodeConfigs, setNodeConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('workflows');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [workflowsRes, templatesRes, nodeTypesRes] = await Promise.all([
        fetch(`${API_URL}/api/workflows`, { headers }),
        fetch(`${API_URL}/api/workflows/templates/list`, { headers }),
        fetch(`${API_URL}/api/workflows/node-types`, { headers })
      ]);

      if (workflowsRes.ok) {
        const data = await workflowsRes.json();
        setWorkflows(data.workflows || []);
      }

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }

      if (nodeTypesRes.ok) {
        const data = await nodeTypesRes.json();
        setNodeConfigs(data.nodes || {});
      }
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const createWorkflow = async (name, triggerType) => {
    try {
      const res = await fetch(`${API_URL}/api/workflows`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, trigger_type: triggerType })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Workflow created');
        await fetchData();
        setShowCreateModal(false);
        // Fetch the full workflow and open editor
        const workflowRes = await fetch(`${API_URL}/api/workflows/${data.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (workflowRes.ok) {
          setSelectedWorkflow(await workflowRes.json());
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create workflow');
      }
    } catch (error) {
      toast.error('Failed to create workflow');
    }
  };

  const applyTemplate = async (templateId, name) => {
    try {
      const res = await fetch(`${API_URL}/api/workflows/templates/${templateId}/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Template applied!');
        await fetchData();
        // Open the editor
        const workflowRes = await fetch(`${API_URL}/api/workflows/${data.workflow_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (workflowRes.ok) {
          setSelectedWorkflow(await workflowRes.json());
        }
      } else {
        toast.error('Failed to apply template');
      }
    } catch (error) {
      toast.error('Failed to apply template');
    }
  };

  const toggleWorkflowStatus = async (workflowId, currentStatus) => {
    const endpoint = currentStatus === 'active' ? 'pause' : 'activate';
    try {
      const res = await fetch(`${API_URL}/api/workflows/${workflowId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(`Workflow ${endpoint}d`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || `Failed to ${endpoint} workflow`);
      }
    } catch (error) {
      toast.error(`Failed to ${endpoint} workflow`);
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!window.confirm('Delete this workflow?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/workflows/${workflowId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Workflow deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'paused': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'draft': return 'bg-slate-500/20 text-emerald-300/70 border-slate-500/30';
      default: return 'bg-slate-500/20 text-emerald-300/70 border-slate-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">{getBusinessTypeName()} Automations</h1>
        <p className="text-emerald-300/70">Build drag-and-drop automation workflows with triggers, actions, and conditions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Zap className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{workflows.length}</p>
                <p className="text-sm text-emerald-300/70">Total Workflows</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Play className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {workflows.filter(w => w.status === 'active').length}
                </p>
                <p className="text-sm text-emerald-300/70">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <GitBranch className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {workflows.reduce((acc, w) => acc + (w.total_executions || 0), 0)}
                </p>
                <p className="text-sm text-emerald-300/70">Total Executions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['workflows', 'templates'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab 
                ? 'bg-emerald-600 text-white' 
                : 'bg-[#0a3d2e] text-emerald-300/70 hover:bg-slate-700'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab === 'workflows' ? 'My Workflows' : 'Templates'}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'workflows' && (
          <motion.div
            key="workflows"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">Your Workflows</h2>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="create-workflow-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Workflow
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
              </div>
            ) : workflows.length === 0 ? (
              <Card className="bg-[#052e22]/50 border-emerald-800/30 border-dashed">
                <CardContent className="p-12 text-center">
                  <Zap className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No workflows yet</h3>
                  <p className="text-emerald-300/70 mb-4">Create your first automation workflow</p>
                  <div className="flex gap-2 justify-center">
                    <Button 
                      onClick={() => setActiveTab('templates')}
                      variant="outline"
                      className="border-emerald-800/30 text-slate-300"
                    >
                      Browse Templates
                    </Button>
                    <Button 
                      onClick={() => setShowCreateModal(true)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      Create from Scratch
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {workflows.map((workflow) => (
                  <Card 
                    key={workflow.id} 
                    className="bg-[#052e22]/50 border-emerald-800/30 hover:border-emerald-800/30 transition-all cursor-pointer"
                    onClick={() => setSelectedWorkflow(workflow)}
                    data-testid={`workflow-card-${workflow.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-white">{workflow.name}</h3>
                          <p className="text-sm text-emerald-300/70 mt-1">{workflow.description || 'No description'}</p>
                        </div>
                        <Badge className={`${getStatusColor(workflow.status)} border`}>
                          {workflow.status}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-emerald-300/70 mb-4">
                        <div className="flex items-center gap-1">
                          <GitBranch className="w-4 h-4" />
                          <span>{workflow.nodes?.length || 0} nodes</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Zap className="w-4 h-4" />
                          <span>{workflow.total_executions || 0} runs</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {workflow.status === 'draft' || workflow.status === 'paused' ? (
                          <Button
                            size="sm"
                            onClick={() => toggleWorkflowStatus(workflow.id, workflow.status)}
                            className="bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30"
                            data-testid={`activate-workflow-${workflow.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" /> Activate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => toggleWorkflowStatus(workflow.id, workflow.status)}
                            className="bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30"
                          >
                            <Pause className="w-4 h-4 mr-1" /> Pause
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-300/70 hover:text-white"
                          onClick={() => setSelectedWorkflow(workflow)}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => deleteWorkflow(workflow.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'templates' && (
          <motion.div
            key="templates"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <h2 className="text-xl font-semibold text-white mb-6">Workflow Templates</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card 
                  key={template.id}
                  className="bg-[#052e22]/50 border-emerald-800/30 hover:border-emerald-500/50 transition-all"
                  data-testid={`template-card-${template.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <Zap className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{template.name}</h3>
                        <Badge variant="outline" className="text-xs text-emerald-300/70 border-emerald-800/30">
                          {template.category}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-emerald-300/70 mb-4">{template.description}</p>

                    <Button
                      onClick={() => applyTemplate(template.id, template.name)}
                      className="w-full bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 border border-emerald-500/30"
                      data-testid={`use-template-${template.id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Use Template
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Workflow Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateWorkflowModal
            nodeConfigs={nodeConfigs}
            onClose={() => setShowCreateModal(false)}
            onCreate={createWorkflow}
          />
        )}
      </AnimatePresence>

      {/* Workflow Editor */}
      <AnimatePresence>
        {selectedWorkflow && (
          <WorkflowEditor
            workflow={selectedWorkflow}
            nodeConfigs={nodeConfigs}
            token={token}
            onClose={() => {
              setSelectedWorkflow(null);
              fetchData();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Create Workflow Modal
function CreateWorkflowModal({ nodeConfigs, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [triggerType, setTriggerType] = useState('trigger_manual');

  const triggerOptions = useMemo(() => 
    Object.entries(nodeConfigs)
      .filter(([key]) => key.startsWith('trigger_'))
      .map(([key, config]) => ({ value: key, label: config.label })),
    [nodeConfigs]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a workflow name');
      return;
    }
    onCreate(name, triggerType);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#052e22] border border-emerald-800/30 rounded-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-semibold text-white mb-4">Create New Workflow</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-emerald-300/70 mb-2">Workflow Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Welcome New Leads"
              className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              data-testid="workflow-name-input"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-emerald-300/70 mb-2">Trigger</label>
            <select
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value)}
              className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white focus:outline-none focus:border-emerald-500"
              data-testid="trigger-select"
            >
              {triggerOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-emerald-300/70">
              Cancel
            </Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-workflow-submit">
              Create Workflow
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Workflow Editor with ReactFlow
function WorkflowEditor({ workflow, nodeConfigs, token, onClose }) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [saving, setSaving] = useState(false);

  // Convert workflow data to ReactFlow format
  useEffect(() => {
    const flowNodes = (workflow.nodes || []).map(node => ({
      id: node.id,
      type: node.type.startsWith('trigger_') ? 'trigger' : node.type.startsWith('condition_') ? 'condition' : 'action',
      position: node.position || { x: 250, y: 50 },
      data: {
        ...node,
        label: node.label,
        icon: NODE_ICONS[node.type],
        description: getNodeDescription(node, nodeConfigs),
      }
    }));

    const flowEdges = (workflow.connections || []).map(conn => ({
      id: conn.id,
      source: conn.source,
      target: conn.target,
      sourceHandle: conn.source_handle || undefined,
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { strokeWidth: 2, stroke: conn.source_handle === 'no' ? '#ef4444' : '#6366f1' }
    }));

    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [workflow, nodeConfigs]);

  const onNodesChange = useCallback((changes) => {
    setNodes(nds => applyNodeChanges(changes, nds));
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges(eds => applyEdgeChanges(changes, eds));
  }, []);

  const onConnect = useCallback((params) => {
    const newEdge = {
      ...params,
      id: `edge-${Date.now()}`,
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { strokeWidth: 2, stroke: params.sourceHandle === 'no' ? '#ef4444' : '#6366f1' }
    };
    setEdges(eds => addEdge(newEdge, eds));
  }, []);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const saveWorkflow = async () => {
    setSaving(true);
    try {
      // Convert back to workflow format
      const workflowNodes = nodes.map(node => ({
        id: node.id,
        type: node.data.type,
        label: node.data.label,
        position: node.position,
        config: node.data.config || {}
      }));

      const workflowConnections = edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        source_handle: edge.sourceHandle || null
      }));

      const res = await fetch(`${API_URL}/api/workflows/${workflow.id}/canvas`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nodes: workflowNodes, connections: workflowConnections })
      });

      if (res.ok) {
        toast.success('Workflow saved');
      } else {
        toast.error('Failed to save workflow');
      }
    } catch (error) {
      toast.error('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const addNode = (type) => {
    const config = nodeConfigs[type] || {};
    const newNode = {
      id: `${type.split('_')[0]}-${Date.now()}`,
      type: type.startsWith('trigger_') ? 'trigger' : type.startsWith('condition_') ? 'condition' : 'action',
      position: { x: 250, y: (nodes.length * 120) + 50 },
      data: {
        type,
        label: config.label || type,
        icon: NODE_ICONS[type],
        config: {},
        description: ''
      }
    };
    setNodes(nds => [...nds, newNode]);
  };

  const updateNodeConfig = (nodeId, newConfig) => {
    setNodes(nds => nds.map(node => {
      if (node.id === nodeId) {
        const updatedData = { ...node.data, config: { ...node.data.config, ...newConfig } };
        updatedData.description = getNodeDescription({ ...node.data, config: updatedData.config }, nodeConfigs);
        return { ...node, data: updatedData };
      }
      return node;
    }));
  };

  const deleteNode = (nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    if (selectedNode?.id === nodeId) setSelectedNode(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-950 z-50"
    >
      {/* Header */}
      <div className="h-14 border-b border-emerald-800/30 flex items-center justify-between px-4 bg-[#052e22]/50">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onClose} className="text-emerald-300/70 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="font-semibold text-white">{workflow.name}</h2>
            <Badge className={workflow.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-emerald-300/70'}>
              {workflow.status}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={saveWorkflow}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-workflow-btn"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-56px)]">
        {/* Node Palette */}
        <div className="w-64 border-r border-emerald-800/30 bg-[#052e22]/30 p-4 overflow-y-auto">
          <h3 className="text-sm font-semibold text-slate-300 mb-4">Add Nodes</h3>
          
          <div className="mb-6">
            <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Actions</p>
            <div className="space-y-1">
              {Object.entries(nodeConfigs)
                .filter(([key]) => key.startsWith('action_'))
                .map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => addNode(key)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#0a3d2e]/50 hover:bg-blue-500/20 border border-emerald-800/30/50 hover:border-blue-500/50 text-left text-sm text-slate-300 transition-all"
                    data-testid={`add-node-${key}`}
                  >
                    <div className="p-1 rounded bg-blue-500/20">
                      {NODE_ICONS[key]}
                    </div>
                    <span>{config.label}</span>
                  </button>
                ))}
            </div>
          </div>

          <div className="mb-6">
            <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Conditions</p>
            <div className="space-y-1">
              {Object.entries(nodeConfigs)
                .filter(([key]) => key.startsWith('condition_'))
                .map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => addNode(key)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#0a3d2e]/50 hover:bg-emerald-500/20 border border-emerald-800/30/50 hover:border-emerald-500/50 text-left text-sm text-slate-300 transition-all"
                    data-testid={`add-node-${key}`}
                  >
                    <div className="p-1 rounded bg-emerald-500/20">
                      {NODE_ICONS[key]}
                    </div>
                    <span>{config.label}</span>
                  </button>
                ))}
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            className="bg-slate-950"
          >
            <Background color="#334155" gap={20} />
            <Controls className="!bg-[#0a3d2e] !border-emerald-800/30 !rounded-lg [&_button]:!bg-slate-700 [&_button]:!border-slate-600 [&_button:hover]:!bg-slate-600" />
          </ReactFlow>
        </div>

        {/* Properties Panel */}
        {selectedNode && (
          <div className="w-80 border-l border-emerald-800/30 bg-[#052e22]/30 p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white">Node Settings</h3>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-400 hover:text-red-300"
                onClick={() => deleteNode(selectedNode.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-emerald-300/70 mb-1">Node Type</label>
                <p className="text-white font-medium">{nodeConfigs[selectedNode.data.type]?.label || selectedNode.data.type}</p>
              </div>

              {/* Dynamic config fields */}
              {nodeConfigs[selectedNode.data.type]?.config_fields?.map(field => (
                <div key={field.name}>
                  <label className="block text-sm text-emerald-300/70 mb-2">{field.label}</label>
                  {field.type === 'text' && (
                    <input
                      type="text"
                      value={selectedNode.data.config?.[field.name] || ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { [field.name]: e.target.value })}
                      className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                      placeholder={field.placeholder || ''}
                    />
                  )}
                  {field.type === 'textarea' && (
                    <textarea
                      value={selectedNode.data.config?.[field.name] || ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { [field.name]: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm resize-none focus:outline-none focus:border-emerald-500"
                      placeholder={field.placeholder || ''}
                    />
                  )}
                  {field.type === 'number' && (
                    <input
                      type="number"
                      value={selectedNode.data.config?.[field.name] || ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { [field.name]: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                    />
                  )}
                  {field.type === 'select' && (
                    <select
                      value={selectedNode.data.config?.[field.name] || ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { [field.name]: e.target.value })}
                      className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                    >
                      <option value="">Select...</option>
                      {field.options?.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  )}
                  {field.type === 'checkbox' && (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedNode.data.config?.[field.name] || false}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { [field.name]: e.target.checked })}
                        className="rounded border-slate-600 bg-[#0a3d2e] text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-slate-300">Enable</span>
                    </label>
                  )}
                </div>
              ))}

              {/* Variable hints */}
              {(selectedNode.data.type === 'action_send_email' || selectedNode.data.type === 'action_notify_team') && (
                <div className="p-3 bg-[#0a3d2e]/50 rounded-lg border border-emerald-800/30/50">
                  <p className="text-xs text-emerald-300/70 mb-2">Available variables:</p>
                  <div className="flex flex-wrap gap-1">
                    {['{name}', '{email}', '{company}', '{score}'].map(v => (
                      <code key={v} className="px-2 py-0.5 bg-slate-700 rounded text-xs text-emerald-300">{v}</code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Helper function to get node description from config
function getNodeDescription(node, nodeConfigs) {
  const config = node.config || {};
  const type = node.type;

  if (type === 'action_send_email') {
    return config.subject ? `Subject: ${config.subject.substring(0, 30)}...` : '';
  }
  if (type === 'action_wait') {
    return config.duration && config.unit ? `Wait ${config.duration} ${config.unit}` : '';
  }
  if (type === 'action_add_tag' || type === 'action_remove_tag') {
    return config.tag ? `Tag: ${config.tag}` : '';
  }
  if (type === 'action_update_lead') {
    return config.field && config.value ? `${config.field} = ${config.value}` : '';
  }
  if (type === 'condition_score_check') {
    return config.threshold ? `Score ${config.operator || '>='} ${config.threshold}` : '';
  }
  if (type === 'condition_tag_check') {
    return config.tag ? `Has tag: ${config.tag}` : '';
  }

  return '';
}
